import React, { useEffect, useState, useCallback, startTransition } from 'react';
import {
    Box, Heading, VStack, Text, Flex, Button, useToast, HStack, ButtonGroup, Grid, Icon,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, SimpleGrid, Tag,
    Skeleton, SkeletonText, Spinner,
    Input, InputGroup, InputLeftElement, InputRightElement, IconButton, Tooltip,
} from '@chakra-ui/react';
import { FaList, FaTh, FaSearch, FaTimes, FaChartBar } from 'react-icons/fa';

import Preloader from "../components/Preloader";
import ScrollToTop from "../components/ScrollToTop";
import KanbanReturnCard from '../components/KanbanReturnCard';
import KanbanFullDetails from '../components/KanbanFullDetails';
import ReturnCard from '../components/ReturnCard';
import DevolucoesStatsPanel from '../components/DevolucoesStatsPanel';
import DevolucoesUrgentBanner from '../components/DevolucoesUrgentBanner';
import DevolucoesUrgencyFilters from '../components/DevolucoesUrgencyFilters';
import DevolucoesMetricas from '../components/DevolucoesMetricas';
import { DevolucoesFiltersList, DevolucoesFiltersKanban } from '../components/DevolucoesFilters';
import { useConfirm } from '../components/ConfirmDialog';
import {
    formatDateTime, formatDate, getStatusDevolucaoColor, getStatusDevolucaoLabel,
    getStatusReclamacaoColor, getStatusReclamacaoLabel,
    STATUS_DEVOLUCAO_META, STATUS_RECLAMACAO_META,
} from '../utils/formatters';

import dayjs from "dayjs";
import "dayjs/locale/pt-br";

import {
    getDevolucoesML, setDevolucaoAvariaML, setDevolucaoRecebimentoML, anexarArquivosDevolucao,
    getDevolucoesKanban, getDevolucaoDetalhes,
    setDevolucaoEstoque,
    reverterDevolucaoEstoque, finalizarDevolucaoManual,
    marcarRevisaoOk, marcarRevisaoFalha, consultarCustoEnvioDevolucao,
    sincronizarDevolucao,
    excluirDevolucaoManual, atualizarDevolucaoManual,
} from "../services/api";
import NovaDevolucaoManualModal from '../components/NovaDevolucaoManualModal';
import EditarDevolucaoManualModal from '../components/EditarDevolucaoManualModal';
import BarcodeScanner from '../components/BarcodeScanner';

const InfoHeaderRow = ({ label, value, children }) => (
    <Box>
        <Text fontSize="10px" color="gray.500" fontWeight="medium" textTransform="uppercase">
            {label}
        </Text>
        {children ? children : (
            <Text fontSize="sm" color="gray.800" fontWeight="bold">
                {value || 'N/A'}
            </Text>
        )}
    </Box>
);

const KanbanSkeleton = () => (
    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={{ base: 4, md: 6 }}>
        {[1, 2, 3].map((col) => (
            <VStack
                key={col}
                align="stretch"
                bg="gray.100"
                p={{ base: 2, md: 4 }}
                borderRadius="lg"
                boxShadow="inner"
                h="70vh"
            >
                <Skeleton height="20px" w="150px" mb={4} mx="auto" />
                <VStack spacing={3} align="stretch" flex="1" p={1}>
                    {[1, 2, 3, 4].map((item) => (
                        <Box key={item} bg="white" p={3} borderRadius="lg" boxShadow="md" minH="110px">
                            <HStack spacing={3}>
                                <Skeleton boxSize="60px" borderRadius="md" />
                                <VStack align="stretch" flex={1} spacing={2}>
                                    <SkeletonText noOfLines={3} spacing="3" />
                                </VStack>
                            </HStack>
                        </Box>
                    ))}
                </VStack>
            </VStack>
        ))}
    </Grid>
);

const findItemInColumns = (columns, itemId) => {
    if (!columns || !itemId) return null;
    for (const colId of Object.keys(columns)) {
        if (columns[colId] && columns[colId].items) {
            const item = columns[colId].items.find(i => i.id === itemId);
            if (item) return item;
        }
    }
    return null;
};

const DevolucoesPage = () => {
    const [devolucoes, setDevolucoes] = useState([]);
    const [pagina, setPagina] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [idVenda, setIdVenda] = useState("");
    const [status, setStatus] = useState("");
    const [filtroConta, setFiltroConta] = useState("");
    const [statusReceb, setStatusReceb] = useState("");
    const [statusRev, setStatusRev] = useState("");
    const [ateHoje, setAteHoje] = useState(false);
    const [statusReemb, setStatusReemb] = useState("");
    const [dataInicial, setDataInicial] = useState("");
    const [dataFinal, setDataFinal] = useState("");
    const [moneyOwner, setMoneyOwner] = useState("");

    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('kanban');
    const [urgentList, setUrgentList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDevolucao, setSelectedDevolucao] = useState(null);
    const [isModalLoading, setIsModalLoading] = useState(false);

    const [rawKanbanData, setRawKanbanData] = useState(null);
    const [filteredKanbanColumns, setFilteredKanbanColumns] = useState(null);
    const [kanbanSearch, setKanbanSearch] = useState("");
    const [kanbanSearchDebounced, setKanbanSearchDebounced] = useState("");
    const [scannerOpen, setScannerOpen] = useState(false);
    const [kanbanStartDate, setKanbanStartDate] = useState("");
    const [kanbanEndDate, setKanbanEndDate] = useState("");
    const [kanbanStatus, setKanbanStatus] = useState("");
    const [kanbanStatusReclamacao, setKanbanStatusReclamacao] = useState("");
    const [kanbanOrigem, setKanbanOrigem] = useState("");
    const [kanbanMoneyOwner, setKanbanMoneyOwner] = useState("");
    const [urgencia, setUrgencia] = useState("");
    const [kanbanUrgencia, setKanbanUrgencia] = useState("");
    const [comDivergencia, setComDivergencia] = useState(false);
    const [kanbanComDivergencia, setKanbanComDivergencia] = useState(false);
    const [kanbanOrdem, setKanbanOrdem] = useState("padrao");
    const [colSearches, setColSearches] = useState({
        'col-1-devolucao': '',
        'col-2-recebida': '',
        'col-3-estoque': '',
    });
    const [colSearchesDebounced, setColSearchesDebounced] = useState({
        'col-1-devolucao': '',
        'col-2-recebida': '',
        'col-3-estoque': '',
    });
    const [finalizadasMeta, setFinalizadasMeta] = useState({ page: 1, total: 0, has_more: false, search: '' });
    const [loadingMoreFinalizadas, setLoadingMoreFinalizadas] = useState(false);
    const [kanbanVisible, setKanbanVisible] = useState({
        'col-1-devolucao': 30,
        'col-2-recebida': 30,
        'col-3-estoque': 30,
    });

    useEffect(() => {
        const t = setTimeout(() => setColSearchesDebounced(colSearches), 350);
        return () => clearTimeout(t);
    }, [colSearches]);

    useEffect(() => {
        const t = setTimeout(() => setKanbanSearchDebounced(kanbanSearch), 350);
        return () => clearTimeout(t);
    }, [kanbanSearch]);

    const [isFilterExpanded, setIsFilterExpanded] = useState(false);
    const [isNovaManualOpen, setIsNovaManualOpen] = useState(false);
    const [isEditarManualOpen, setIsEditarManualOpen] = useState(false);

    const toast = useToast();
    const { confirm, ConfirmPortal } = useConfirm();

    const showToast = (title, status = 'info', description = '') => {
        toast({
            title,
            description,
            status,
            duration: status === 'error' ? 6000 : 3500,
            isClosable: true,
            position: 'top-right',
        });
    };

    useEffect(() => {
        if (!rawKanbanData) {
            setFilteredKanbanColumns(null);
            return;
        }

        const searchLower = kanbanSearchDebounced.toLowerCase();

        const matchesSearch = (item, term) => {
            if (!term) return true;
            const t = term.toLowerCase();
            const order = item.orders?.[0] || {};
            const fields = [
                item.id, item.id_ml, item.id_order_ml, item.id_venda, item.id_origem, item.pack_id_ml, item.claim_id,
                order.order_id, order.item_id,
                order.nome, order.sku, order.codigo_global, order.codigo_fornecedor, order.codigo_fabricante,
                `${item.dados_cliente?.first_name || ''} ${item.dados_cliente?.last_name || ''}`,
                item.dados_cliente?.business_name,
                item.dados_cliente?.doc_number,
                ...(item.shipment_ids || []),
            ];
            return fields.some(f => f != null && String(f).toLowerCase().includes(t));
        };

        const newFilteredColumns = {};
        let allItems = [];

        for (const colId in rawKanbanData) {
            const column = rawKanbanData[colId];
            const colTerm = (colSearchesDebounced[colId] || '').trim();
            const aplicaColLocal = colId !== 'col-3-estoque';
            const filteredItems = column.items.filter(item => {
                if (searchLower && !matchesSearch(item, searchLower)) return false;
                if (aplicaColLocal && colTerm && !matchesSearch(item, colTerm)) return false;
                if (kanbanStatus && item.status !== kanbanStatus) return false;
                if (kanbanStatusReclamacao && item.status_reclamacao !== kanbanStatusReclamacao) return false;
                if (kanbanOrigem) {
                    const itemOrigem = (item.origem || 'ml').toLowerCase();
                    if (kanbanOrigem === 'taga' || kanbanOrigem === 'ecommerce') {
                        if (itemOrigem !== kanbanOrigem) return false;
                    } else if (kanbanOrigem === 'ml_1') {
                        if (itemOrigem !== 'ml' || String(item.conta) !== '1') return false;
                    } else if (kanbanOrigem === 'ml_2') {
                        if (itemOrigem !== 'ml' || String(item.conta) !== '2') return false;
                    } else if (kanbanOrigem === 'ml_3') {
                        if (itemOrigem !== 'ml' || String(item.conta) !== '3') return false;
                    }
                }
                if (kanbanMoneyOwner && (item.money_owner || 'unknown') !== kanbanMoneyOwner) return false;
                if (kanbanUrgencia && (item.urgency_level || 'sem_prazo') !== kanbanUrgencia) return false;
                if (kanbanComDivergencia && !item.divergencia) return false;
                if (kanbanStartDate || kanbanEndDate) {
                    const itemDate = dayjs(item.date_created);
                    if (kanbanStartDate && itemDate.isBefore(dayjs(kanbanStartDate))) return false;
                    if (kanbanEndDate && itemDate.isAfter(dayjs(kanbanEndDate).endOf('day'))) return false;
                }
                return true;
            });
            newFilteredColumns[colId] = { ...column, items: filteredItems };
            allItems = allItems.concat(filteredItems);
        }

        // urgentList é populado diretamente do backend em handlePesquisar (meta.urgent_items),
        // então NÃO sobrescrevemos aqui — manteria a lista completa mesmo após filtros locais.
        setFilteredKanbanColumns(newFilteredColumns);
        setKanbanVisible({
            'col-1-devolucao': 30,
            'col-2-recebida': 30,
            'col-3-estoque': 30,
        });
    }, [kanbanSearchDebounced, kanbanStartDate, kanbanEndDate, kanbanStatus, kanbanStatusReclamacao, kanbanOrigem, kanbanMoneyOwner, kanbanUrgencia, kanbanComDivergencia, colSearchesDebounced, rawKanbanData]);

    useEffect(() => {
        if (!filteredKanbanColumns) return;
        const STEP = 30;
        let raf;
        const tick = () => {
            setKanbanVisible(prev => {
                const next = { ...prev };
                let pendente = false;
                for (const id in filteredKanbanColumns) {
                    const total = filteredKanbanColumns[id].items.length;
                    const atual = prev[id] ?? STEP;
                    if (atual < total) {
                        next[id] = Math.min(total, atual + STEP);
                        pendente = true;
                    }
                }
                if (pendente) raf = requestAnimationFrame(tick);
                return next;
            });
        };
        raf = requestAnimationFrame(tick);
        return () => { if (raf) cancelAnimationFrame(raf); };
    }, [filteredKanbanColumns]);

    const runModalAction = async (fn, { ok, err, refetchId = true, confirmOpts = null }) => {
        if (!selectedDevolucao) return;
        if (confirmOpts) {
            const ok2 = await confirm(confirmOpts);
            if (!ok2) return;
        }
        const { id } = selectedDevolucao;
        setIsModalLoading(true);
        try {
            const response = await fn(id);
            if (response && response.success) {
                const title = typeof ok === 'function' ? ok(response) : (response.message || ok);
                showToast(title, 'success');
                await handlePesquisar(viewMode === 'list' ? pagina : 1, refetchId ? id : null);
            } else {
                const detail = response?.error || (response?.http_code ? `HTTP ${response.http_code}` : 'Resposta inesperada do servidor.');
                showToast(err, 'error', detail);
            }
        } catch (e) {
            showToast(err, 'error', e?.message || 'Erro desconhecido');
        } finally {
            setIsModalLoading(false);
        }
    };

    const handleFinalizarManual = (obs) => runModalAction(
        (id) => finalizarDevolucaoManual(id, obs),
        { ok: 'Devolução finalizada com sucesso.', err: 'Erro ao finalizar' }
    );

    const handleIncluirEstoque = (payload) => runModalAction(
        (id) => setDevolucaoEstoque(id, payload),
        {
            ok: 'Item incluído no estoque.',
            err: 'Erro ao incluir no estoque',
            confirmOpts: {
                title: 'Incluir no estoque?',
                body: `A devolução será marcada como "em estoque" com quantidade ${payload?.qtd ?? '-'} na localização ${payload?.loc ?? '-'}. A quantidade informada será somada ao estoque TAGA.`,
                confirmText: 'Confirmar Inclusão',
                cancelText: 'Cancelar',
                confirmColor: 'green',
            },
        }
    );

    const handleReverterEstoque = () => runModalAction(
        (id) => reverterDevolucaoEstoque(id),
        {
            ok: 'Estoque revertido com sucesso.',
            err: 'Erro ao reverter estoque',
            confirmOpts: {
                title: 'Reverter estoque?',
                body: 'A quantidade será removida do estoque e os dados de localização serão apagados. Esta ação não pode ser desfeita automaticamente.',
                confirmText: 'Reverter',
                cancelText: 'Cancelar',
                confirmColor: 'red',
            },
        }
    );

    const handleRevisaoOk = () => runModalAction(
        (id) => marcarRevisaoOk(id),
        {
            ok: 'Revisão OK enviada ao Mercado Livre.',
            err: 'Erro ao enviar revisão OK',
            confirmOpts: {
                title: 'Confirmar "Chegou OK"?',
                body: 'Será enviada ao Mercado Livre a confirmação de que o produto chegou como esperado. Esta ação altera o status da devolução no painel ML e não pode ser desfeita.',
                confirmText: 'Enviar ao ML',
                cancelText: 'Cancelar',
                confirmColor: 'green',
            },
        }
    );

    const handleRevisaoFalha = ({ reason, message, attachments = [], order_id = null }) => runModalAction(
        (id) => marcarRevisaoFalha(id, { reason, message, attachments, order_id }),
        {
            ok: 'Revisão com falha enviada ao Mercado Livre.',
            err: 'Erro ao enviar revisão com falha',
            confirmOpts: {
                title: 'Confirmar revisão com falha?',
                body: `Razão: ${reason}. Esta ação será enviada ao Mercado Livre e não pode ser desfeita.`,
                confirmText: 'Enviar ao ML',
                cancelText: 'Cancelar',
                confirmColor: 'red',
            },
        }
    );

    const handleSincronizarDevolucao = () => runModalAction(
        (id) => sincronizarDevolucao(id),
        {
            ok: (response) => response.message || 'Devolução sincronizada com o Mercado Livre.',
            err: 'Erro ao sincronizar devolução',
        }
    );

    const handleExcluirManual = async () => {
        if (!selectedDevolucao?.id) return;
        const ok = await confirm({
            title: 'Excluir devolução manual?',
            body: 'Esta ação é permanente e removerá a devolução do sistema.',
            confirmText: 'Excluir',
            cancelText: 'Cancelar',
            confirmColor: 'red',
        });
        if (!ok) return;
        setIsModalLoading(true);
        try {
            const resp = await excluirDevolucaoManual(selectedDevolucao.id);
            if (resp?.success) {
                showToast('Devolução excluída', 'success');
                closeModal();
                await handlePesquisar(1);
            } else {
                showToast('Falha ao excluir', 'error', resp?.error || '');
            }
        } finally { setIsModalLoading(false); }
    };

    const handleAtualizarStatusManual = async (novoStatus) => {
        if (!selectedDevolucao?.id) return;
        setIsModalLoading(true);
        try {
            const resp = await atualizarDevolucaoManual(selectedDevolucao.id, { status: novoStatus });
            if (resp?.success) {
                showToast('Status atualizado', 'success');
                await handlePesquisar(viewMode === 'list' ? pagina : 1, selectedDevolucao.id);
            } else {
                showToast('Falha ao atualizar status', 'error', resp?.error || '');
            }
        } finally { setIsModalLoading(false); }
    };

    const handleManualEditado = async () => {
        if (selectedDevolucao?.id) {
            await handlePesquisar(viewMode === 'list' ? pagina : 1, selectedDevolucao.id);
        }
    };

    const handleConsultarCustoEnvio = () => runModalAction(
        (id) => consultarCustoEnvioDevolucao(id),
        {
            ok: (response) => {
                const valor = response.shipping_cost != null
                    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(response.shipping_cost))
                    : 'sem custo retornado';
                return `Custo de envio: ${valor}`;
            },
            err: 'Erro ao consultar custo de envio',
        }
    );

    const handlePesquisar = async (page = 1, updatedItemId = null, ordemOverride = undefined, comDivergenciaOverride = undefined) => {
        if (!isModalLoading) setLoading(true);

        const comDivergenciaEfetivoList = comDivergenciaOverride !== undefined ? comDivergenciaOverride : comDivergencia;
        const comDivergenciaEfetivoKanban = comDivergenciaOverride !== undefined ? comDivergenciaOverride : kanbanComDivergencia;

        const params = new URLSearchParams();

        if (viewMode === 'list') {
            params.append("per_page", perPage);
            params.append("page", page);
            if (idVenda) params.append("id_venda", idVenda);
            if (status) params.append("status", status);
            if (filtroConta) params.append("filtroConta", filtroConta);
            if (statusReceb) params.append("status_recebimento", statusReceb);
            if (statusRev) params.append("status_revisao", statusRev);
            if (ateHoje) params.append("ate_hoje", "true");
            if (statusReemb) params.append("status_reembolso", statusReemb);
            if (moneyOwner) params.append("money_owner", moneyOwner);
            if (urgencia) params.append("urgencia", urgencia);
            if (comDivergenciaEfetivoList) params.append("com_divergencia", "1");
            params.append("data_inicial", dataInicial);
            params.append("data_final", dataFinal);
        }

        try {
            let resp;
            if (viewMode === 'kanban') {
                const kanbanParams = new URLSearchParams();
                const finalizadaSearch = (colSearches['col-3-estoque'] || '').trim();
                if (finalizadaSearch) kanbanParams.append('finalizada_search', finalizadaSearch);
                kanbanParams.append('finalizada_page', '1');
                const ordemEfetiva = ordemOverride !== undefined ? ordemOverride : kanbanOrdem;
                if (ordemEfetiva && ordemEfetiva !== 'padrao') kanbanParams.append('ordem', ordemEfetiva);
                if (comDivergenciaEfetivoKanban) kanbanParams.append('com_divergencia', '1');
                resp = await getDevolucoesKanban(kanbanParams.toString());
                if (!resp) throw new Error("Erro ao buscar dados do Kanban");

                setDados(resp.meta);
                setFinalizadasMeta(resp.meta?.finalizadas || { page: 1, total: 0, has_more: false, search: '' });
                // Lista completa de urgentes vem do backend (independente de paginação)
                setUrgentList(resp.meta?.urgent_items || []);
                const newCols = {
                    'col-1-devolucao': { id: 'col-1-devolucao', title: 'Em Devolução', items: resp.data['col-1-devolucao'] || [] },
                    'col-2-recebida': { id: 'col-2-recebida', title: 'Recebida', items: resp.data['col-2-recebida'] || [] },
                    'col-3-estoque': { id: 'col-3-estoque', title: 'Finalizada', items: resp.data['col-3-estoque'] || [] },
                };
                startTransition(() => {
                    setRawKanbanData(newCols);
                });
                setDevolucoes([]);

                if (updatedItemId) {
                    const updatedItem = findItemInColumns(newCols, updatedItemId);
                    if (updatedItem) {
                        setSelectedDevolucao(prev => {
                            if (!prev || prev.id !== updatedItem.id) return updatedItem;
                            const { shipping_address, seller_review, last_sync_error, last_api_sync_at, sync_missing_count } = prev;
                            return { ...updatedItem, shipping_address, seller_review, last_sync_error, last_api_sync_at, sync_missing_count };
                        });
                    }
                    else closeModal();
                }
            } else {
                resp = await getDevolucoesML(params.toString());
                if (!resp) throw new Error("Erro ao buscar devoluções");

                setDados(resp.meta);
                setDevolucoes(resp.data || []);
                setPagina(page);

                setRawKanbanData(null);
                setFilteredKanbanColumns(null);
                setUrgentList(resp.data.filter(dev => dev.urgency_level === 'urgente'));

                if (updatedItemId) {
                    const updatedItem = (resp.data || []).find(i => i.id === updatedItemId);
                    if (updatedItem) setSelectedDevolucao(updatedItem);
                    else closeModal();
                }
            }
        } catch (err) {
            showToast('Erro ao buscar dados', 'error', err?.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        handlePesquisar(1);
    }, [viewMode]);

    useEffect(() => {
        if (viewMode !== 'kanban') return;
        const term = (colSearches['col-3-estoque'] || '').trim();
        if (term === (finalizadasMeta.search || '')) return;
        const h = setTimeout(() => {
            refetchFinalizadas({ page: 1, search: term });
        }, 400);
        return () => clearTimeout(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colSearches['col-3-estoque'], viewMode]);

    useEffect(() => {
        if (viewMode !== 'kanban') return;
        if ((colSearches['col-3-estoque'] || '').trim()) return;
        const term = kanbanSearch.trim();
        if (term === (finalizadasMeta.search || '')) return;
        const h = setTimeout(() => {
            refetchFinalizadas({ page: 1, search: term });
        }, 400);
        return () => clearTimeout(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kanbanSearch, viewMode]);

    const refetchFinalizadas = async ({ page = 1, search = '', append = false } = {}) => {
        if (append) setLoadingMoreFinalizadas(true);
        try {
            const params = new URLSearchParams();
            params.append('only_finalizadas', 'true');
            params.append('finalizada_page', String(page));
            if (search) params.append('finalizada_search', search);
            const resp = await getDevolucoesKanban(params.toString());
            if (!resp) return;
            const items = resp.data?.['col-3-estoque'] || [];
            startTransition(() => {
                setRawKanbanData(prev => {
                    if (!prev) return prev;
                    const novos = append
                        ? [...(prev['col-3-estoque']?.items || []), ...items]
                        : items;
                    return {
                        ...prev,
                        'col-3-estoque': { ...prev['col-3-estoque'], items: novos },
                    };
                });
            });
            setFinalizadasMeta(resp.meta?.finalizadas || { page, total: items.length, has_more: false, search });
        } finally {
            setLoadingMoreFinalizadas(false);
        }
    };

    const loadMoreFinalizadas = () => {
        if (loadingMoreFinalizadas || !finalizadasMeta.has_more) return;
        refetchFinalizadas({
            page: (finalizadasMeta.page || 1) + 1,
            search: finalizadasMeta.search || '',
            append: true,
        });
    };

    useEffect(() => {
        if (viewMode === 'list') {
            handlePesquisar(pagina);
        }
    }, [pagina, perPage]);

    useEffect(() => {
        if (viewMode === 'list' && devolucoes.length > 0) {
            setUrgentList(devolucoes.filter(dev => dev.urgency_level === 'urgente'));
        }
    }, [devolucoes]);

    const handleLocateUrgent = useCallback((dev) => {
        if (!dev?.id) return;
        const source = rawKanbanData || {};
        let colAlvo = null;
        for (const id of Object.keys(source)) {
            if (source[id]?.items?.some(it => it.id === dev.id)) {
                colAlvo = id; break;
            }
        }
        if (!colAlvo) return;
        const termo = String(dev.id_ml || dev.claim_id || dev.id_order_ml || dev.id);
        setKanbanSearch('');
        setColSearches(prev => ({ ...prev, [colAlvo]: termo }));
    }, [rawKanbanData]);

    const handleCardClick = useCallback(async (devolucao) => {
        setSelectedDevolucao(devolucao);
        setIsModalOpen(true);
        setIsModalLoading(true);
        try {
            const detalhes = await getDevolucaoDetalhes(devolucao.id);
            if (detalhes) {
                setSelectedDevolucao(prev => (prev && prev.id === devolucao.id)
                    ? { ...prev, ...detalhes }
                    : prev);
            }
        } finally {
            setIsModalLoading(false);
        }
    }, []);

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedDevolucao(null);
    };

    const handleUpdateInModal = (field, value) => {
        if (!selectedDevolucao) return;
        setIsModalLoading(true);
        if (field === 'received') handleSetRecebimento(selectedDevolucao.id, value, true);
        if (field === 'avaria') handleSetAvaria(selectedDevolucao.id, value, true);
        if (field !== 'received' && field !== 'avaria') setIsModalLoading(false);
    };

    const handleAnexarArquivo = (files, preAnalise) => {
        const hasFiles = files && files.length > 0;
        const hasText = preAnalise && preAnalise.trim() !== '';
        if (!hasFiles && !hasText) {
            showToast('Nenhum arquivo ou análise foi fornecido.', 'warning');
            return;
        }
        if (!selectedDevolucao) return;
        setIsModalLoading(true);
        (async () => {
            try {
                const response = await anexarArquivosDevolucao(selectedDevolucao.id, files, preAnalise);
                if (response !== false) {
                    const titulo = hasFiles
                        ? `${files.length} arquivo(s) anexado(s) com sucesso.`
                        : 'Pré-análise salva com sucesso.';
                    showToast(titulo, 'success');
                    await handlePesquisar(viewMode === 'list' ? pagina : 1, selectedDevolucao.id);
                } else {
                    showToast('Erro ao anexar arquivos ou análise.', 'error');
                }
            } catch (err) {
                showToast('Erro ao anexar arquivos ou análise.', 'error', err?.message);
            } finally {
                setIsModalLoading(false);
            }
        })();
    };

    const handleArquivoRemovido = async (id) => {
        if (!id) return;
        setIsModalLoading(true);
        try {
            await handlePesquisar(viewMode === 'list' ? pagina : 1, id);
        } finally {
            setIsModalLoading(false);
        }
    };

    const handleSetAvaria = (id, statusVal, updateModal = false) => {
        const loader = updateModal ? setIsModalLoading : setLoading;
        loader(true);
        (async () => {
            try {
                const response = await setDevolucaoAvariaML(id, statusVal);
                if (response !== false) {
                    showToast(statusVal ? 'Marcada como "Avaria"' : 'Marcada como "Sem Defeito"', 'success');
                    await handlePesquisar(viewMode === 'list' ? pagina : 1, updateModal ? id : null);
                } else {
                    showToast('Erro ao atualizar status de avaria.', 'error');
                }
            } catch (err) {
                showToast('Erro ao atualizar status de avaria.', 'error', err?.message);
            } finally {
                loader(false);
            }
        })();
    };

    const handleSetRecebimento = (id, statusVal, updateModal = false) => {
        const loader = updateModal ? setIsModalLoading : setLoading;
        loader(true);
        (async () => {
            try {
                const response = await setDevolucaoRecebimentoML(id, statusVal ? 1 : 0);
                if (response !== false) {
                    showToast(statusVal ? 'Marcada como "Recebida"' : 'Marcada como "Não Recebida"', 'success');
                    await handlePesquisar(viewMode === 'list' ? pagina : 1, updateModal ? id : null);
                } else {
                    showToast('Erro ao atualizar recebimento.', 'error');
                }
            } catch (err) {
                showToast('Erro ao atualizar recebimento.', 'error', err?.message);
            } finally {
                loader(false);
            }
        })();
    };

    const irParaProximaPagina = () => {
        if (dados?.paginacao?.tem_proxima) setPagina((old) => old + 1);
    };
    const irParaPaginaAnterior = () => {
        if (dados?.paginacao?.tem_anterior) setPagina((old) => old - 1);
    };

    const limparFiltros = () => {
        setIdVenda(""); setStatus(""); setFiltroConta(""); setStatusReceb("");
        setStatusRev(""); setAteHoje(false); setStatusReemb(""); setDataInicial("");
        setDataFinal(""); setPerPage(25); setMoneyOwner(""); setUrgencia("");
        setComDivergencia(false);
        setTimeout(() => handlePesquisar(1), 0);
    };

    const limparFiltrosKanban = () => {
        setKanbanSearch(""); setKanbanStartDate(""); setKanbanEndDate("");
        setKanbanStatus(""); setKanbanStatusReclamacao(""); setKanbanOrigem(""); setKanbanMoneyOwner("");
        setKanbanUrgencia(""); setKanbanComDivergencia(false);
        setColSearches({ 'col-1-devolucao': '', 'col-2-recebida': '', 'col-3-estoque': '' });
        if (kanbanOrdem !== 'padrao') {
            setKanbanOrdem('padrao');
            handlePesquisar(1, null, 'padrao');
        }
    };

    const renderModalHeaderInfo = () => {
        if (!selectedDevolucao) return null;
        const dev = selectedDevolucao;
        const adicional = dev.dados_adicionais || {};

        return (
            <Box p={{ base: 3, md: 4 }} bg="white" borderBottomWidth="1px" borderColor="gray.200" mb={4}>
                <Heading size={{ base: "sm", md: "md" }} mb={3}>
                    Devolução Venda #{dev.id_order_ml || dev.id_origem || dev.id_venda}
                </Heading>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacingX={4} spacingY={3} bg="gray.50" p={3} borderRadius="md">
                    <InfoHeaderRow label="Status Reclamação">
                        {dev.status_reclamacao ? (
                            <Tooltip label={STATUS_RECLAMACAO_META[dev.status_reclamacao]?.description || ''} hasArrow placement="top">
                                <Tag colorScheme={getStatusReclamacaoColor(dev.status_reclamacao)} size="sm">
                                    {getStatusReclamacaoLabel(dev.status_reclamacao)}
                                </Tag>
                            </Tooltip>
                        ) : (
                            <Tag colorScheme="gray" size="sm">N/A</Tag>
                        )}
                    </InfoHeaderRow>
                    <InfoHeaderRow label="Status Devolução">
                        <Tooltip label={STATUS_DEVOLUCAO_META[dev.status]?.description || ''} hasArrow placement="top">
                            <Tag colorScheme={getStatusDevolucaoColor(dev.status)} size="sm">
                                {getStatusDevolucaoLabel(dev.status)}
                            </Tag>
                        </Tooltip>
                    </InfoHeaderRow>
                    <InfoHeaderRow
                        label="Origem"
                        value={
                            dev.origem === 'ml'
                                ? `ML ${dev.conta ?? ''}`.trim()
                                : (dev.origem ? dev.origem.toUpperCase() : 'N/A')
                        }
                    />
                    {dev.origem === 'ml' && <InfoHeaderRow label="ID Devolução" value={dev.id_ml} />}
                    {dev.origem === 'ml' && <InfoHeaderRow label="ID Reclamação" value={dev.claim_id} />}
                    <InfoHeaderRow label="Data Limite Revisão" value={formatDateTime(adicional.due_date)} />
                    <InfoHeaderRow label="Data Criação" value={formatDateTime(dev.date_created)} />
                    <InfoHeaderRow label="Prev. Entrega" value={formatDate(dev.estimated_delivery_time)} />
                </SimpleGrid>
            </Box>
        );
    };

    const renderKanbanView = () => (
        <Box>
            <DevolucoesUrgentBanner urgentList={urgentList} onLocate={handleLocateUrgent} />
            {!filteredKanbanColumns ? <KanbanSkeleton /> : (
                <Grid
                        templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                        gap={{ base: 4, md: 6 }}
                        minH="80vh"
                        alignItems="start"
                    >
                        {Object.values(filteredKanbanColumns).map((column) => {
                            const bg = column.id === 'col-1-devolucao' ? '#FDE6E6'
                                : column.id === 'col-2-recebida' ? '#FEF6C5'
                                : '#DCF6DF';
                            const colSearch = colSearches[column.id] || '';
                            const isFinalizada = column.id === 'col-3-estoque';
                            const contador = isFinalizada
                                ? `${column.items.length}${finalizadasMeta.total ? ` / ${finalizadasMeta.total}` : ''}`
                                : column.items.length;
                            const handleScroll = isFinalizada
                                ? (e) => {
                                    const el = e.currentTarget;
                                    if (finalizadasMeta.has_more && !loadingMoreFinalizadas
                                        && el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
                                        loadMoreFinalizadas();
                                    }
                                }
                                : undefined;
                            return (
                                <VStack key={column.id} align="stretch" bg={bg} borderRadius="lg" boxShadow="inner" maxH={{ base: '82vh', md: '90vh' }} spacing={0}>
                                    <Box position="sticky" top={0} zIndex={2} bg={bg} borderTopRadius="lg" boxShadow="sm">
                                        <Heading
                                            size="sm"
                                            textAlign="center"
                                            color="gray.700"
                                            py={3}
                                        >
                                            {column.title.toUpperCase()} ({contador})
                                        </Heading>
                                        <Box px={{ base: 2, md: 3 }} pb={2}>
                                            <InputGroup size="sm">
                                                <InputLeftElement pointerEvents="none">
                                                    <Icon as={FaSearch} color="gray.400" boxSize={3} />
                                                </InputLeftElement>
                                                <Input
                                                    bg="white"
                                                    borderRadius="md"
                                                    placeholder={isFinalizada ? 'Buscar em toda a base...' : 'Buscar por ID, produto, SKU, cliente...'}
                                                    value={colSearch}
                                                    onChange={(e) => setColSearches(prev => ({ ...prev, [column.id]: e.target.value }))}
                                                />
                                                {colSearch && (
                                                    <InputRightElement>
                                                        <IconButton
                                                            size="xs"
                                                            variant="ghost"
                                                            aria-label="Limpar busca"
                                                            icon={<FaTimes />}
                                                            onClick={() => setColSearches(prev => ({ ...prev, [column.id]: '' }))}
                                                        />
                                                    </InputRightElement>
                                                )}
                                            </InputGroup>
                                        </Box>
                                    </Box>
                                    <VStack
                                        spacing={3}
                                        align="stretch"
                                        p={{ base: 2, md: 4 }}
                                        pt={3}
                                        flex="1"
                                        overflowY="auto"
                                        onScroll={handleScroll}
                                        sx={{
                                            '&::-webkit-scrollbar': { width: '8px' },
                                            '&::-webkit-scrollbar-track': { background: 'transparent' },
                                            '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: '4px' },
                                            '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.25)' },
                                        }}
                                    >
                                        {column.items.slice(0, kanbanVisible[column.id] ?? 30).map((item) => (
                                            <KanbanReturnCard
                                                key={item.id}
                                                devolucao={item}
                                                onClick={handleCardClick}
                                            />
                                        ))}
                                        {(kanbanVisible[column.id] ?? 30) < column.items.length && (
                                            <Flex justify="center" py={2}>
                                                <Spinner size="xs" color="gray.400" />
                                            </Flex>
                                        )}
                                        {isFinalizada && loadingMoreFinalizadas && (
                                            <Flex justify="center" py={3}>
                                                <Spinner size="sm" color="green.500" />
                                            </Flex>
                                        )}
                                        {isFinalizada && !loadingMoreFinalizadas && !finalizadasMeta.has_more && column.items.length > 0 && (
                                            <Text fontSize="11px" color="gray.500" textAlign="center" py={2}>
                                                Fim dos resultados
                                            </Text>
                                        )}
                                    </VStack>
                                </VStack>
                            );
                        })}
                </Grid>
            )}
        </Box>
    );

    const renderListView = () => (
        <>
            <DevolucoesUrgentBanner urgentList={urgentList} onLocate={handleLocateUrgent} />
            {loading ? <Preloader /> : (
                devolucoes.length === 0 ? (
                    <Box textAlign="center" mt={10} py={10} px={4} bg="gray.50" borderRadius="lg" borderWidth="1px" borderColor="gray.200" borderStyle="dashed">
                        <Text fontSize="lg" fontWeight="medium" color="gray.600" mb={2}>
                            Nenhuma devolução encontrada
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                            Ajuste os filtros ou aumente o intervalo de datas para ver mais resultados.
                        </Text>
                    </Box>
                ) : (
                    <VStack mt={4} spacing={4} align="stretch">
                        {devolucoes.map((dev) => (
                            <ReturnCard
                                key={dev.id}
                                devolucao={dev}
                                onUpdate={(id, field, value) => {
                                    if (field === "avaria") handleSetAvaria(id, value, false);
                                    if (field === "received") handleSetRecebimento(id, value, false);
                                }}
                            />
                        ))}
                    </VStack>
                )
            )}

            {dados?.paginacao && (
                <Box mt={4} display="flex" justifyContent="center" alignItems="center">
                    <HStack spacing={2} justify="center" mt={2}>
                        <Button onClick={() => setPagina(1)} isDisabled={!dados.paginacao.tem_anterior}> &laquo; </Button>
                        <Button onClick={irParaPaginaAnterior} isDisabled={!dados.paginacao.tem_anterior}> &lt; </Button>
                        <ButtonGroup isAttached variant="outline" size="sm">
                            {(() => {
                                let startPage = Math.max(pagina - 1, 1);
                                let endPage = startPage + 2;
                                if (endPage > (dados.paginacao.total_paginas || 1)) {
                                    endPage = dados.paginacao.total_paginas || 1;
                                    startPage = Math.max(endPage - 2, 1);
                                }
                                const paginas = [];
                                for (let i = startPage; i <= endPage; i++) paginas.push(i);
                                return paginas.map((num) => (
                                    <Button
                                        key={num}
                                        onClick={() => setPagina(num)}
                                        colorScheme={num === pagina ? "orange" : "gray"}
                                    >
                                        {num}
                                    </Button>
                                ));
                            })()}
                        </ButtonGroup>
                        <Button onClick={irParaProximaPagina} isDisabled={!dados.paginacao.tem_proxima}> &gt; </Button>
                        <Button onClick={() => setPagina(dados.paginacao.total_paginas || 1)} isDisabled={!dados.paginacao.tem_proxima}> &raquo; </Button>
                    </HStack>
                </Box>
            )}
        </>
    );

    return (
        <>
            <ScrollToTop />
            {loading && <Preloader />}

            <Box fontSize="xl" py={10} bg="white" minH="100vh">
                <Box textAlign="center" ml={4} mb={0} fontSize="xl">
                    <Text color="#F68633" fontWeight="bold">DEVOLUÇÕES</Text>
                </Box>
                <Box p={{ base: 2, md: 4 }} maxW="1200px" mx="auto">

                    <DevolucoesStatsPanel dados={dados} />

                    {viewMode === 'kanban' && (
                        <Flex justify="flex-end" mt={2} mb={1}>
                            <Button colorScheme="orange" size="sm" onClick={() => setIsNovaManualOpen(true)}>
                                + Nova devolução manual
                            </Button>
                        </Flex>
                    )}

                    <NovaDevolucaoManualModal
                        isOpen={isNovaManualOpen}
                        onClose={() => setIsNovaManualOpen(false)}
                        onCreated={() => handlePesquisar(1)}
                    />

                    <EditarDevolucaoManualModal
                        isOpen={isEditarManualOpen}
                        onClose={() => setIsEditarManualOpen(false)}
                        devolucao={selectedDevolucao}
                        onUpdated={handleManualEditado}
                    />

                    <BarcodeScanner
                        isOpen={scannerOpen}
                        onClose={() => setScannerOpen(false)}
                        onDetected={(codigo) => {
                            setScannerOpen(false);
                            setKanbanSearch(codigo);
                        }}
                    />

                    {viewMode === 'list' && (
                        <DevolucoesFiltersList
                            idVenda={idVenda} setIdVenda={setIdVenda}
                            filtroConta={filtroConta} setFiltroConta={setFiltroConta}
                            status={status} setStatus={setStatus}
                            statusReceb={statusReceb} setStatusReceb={setStatusReceb}
                            statusRev={statusRev} setStatusRev={setStatusRev}
                            moneyOwner={moneyOwner} setMoneyOwner={setMoneyOwner}
                            ateHoje={ateHoje} setAteHoje={setAteHoje}
                            dataInicial={dataInicial} setDataInicial={setDataInicial}
                            dataFinal={dataFinal} setDataFinal={setDataFinal}
                            onFiltrar={() => handlePesquisar(1)}
                            onLimpar={limparFiltros}
                            isExpanded={isFilterExpanded}
                            onToggleExpanded={() => setIsFilterExpanded(prev => !prev)}
                        />
                    )}

                    {viewMode === 'kanban' && (
                        <DevolucoesFiltersKanban
                            search={kanbanSearch} setSearch={setKanbanSearch}
                            origem={kanbanOrigem} setOrigem={setKanbanOrigem}
                            status={kanbanStatus} setStatus={setKanbanStatus}
                            statusReclamacao={kanbanStatusReclamacao} setStatusReclamacao={setKanbanStatusReclamacao}
                            moneyOwner={kanbanMoneyOwner} setMoneyOwner={setKanbanMoneyOwner}
                            dataInicial={kanbanStartDate} setDataInicial={setKanbanStartDate}
                            dataFinal={kanbanEndDate} setDataFinal={setKanbanEndDate}
                            ordem={kanbanOrdem}
                            setOrdem={(v) => { setKanbanOrdem(v); handlePesquisar(1, null, v); }}
                            onLimpar={limparFiltrosKanban}
                            onScan={() => setScannerOpen(true)}
                            isExpanded={isFilterExpanded}
                            onToggleExpanded={() => setIsFilterExpanded(prev => !prev)}
                        />
                    )}

                    {viewMode === 'list' && (
                        <DevolucoesUrgencyFilters
                            value={urgencia}
                            onChange={(v) => { setUrgencia(v); setTimeout(() => handlePesquisar(1), 0); }}
                            onClear={() => { setUrgencia(''); setTimeout(() => handlePesquisar(1), 0); }}
                            totals={dados?.totais}
                            comDivergencia={comDivergencia}
                            onChangeComDivergencia={(v) => { setComDivergencia(v); handlePesquisar(1, null, undefined, v); }}
                        />
                    )}

                    {viewMode === 'kanban' && (
                        <DevolucoesUrgencyFilters
                            value={kanbanUrgencia}
                            onChange={setKanbanUrgencia}
                            onClear={() => setKanbanUrgencia('')}
                            totals={dados?.totais}
                            comDivergencia={kanbanComDivergencia}
                            onChangeComDivergencia={(v) => { setKanbanComDivergencia(v); handlePesquisar(1, null, undefined, v); }}
                        />
                    )}

                    <Flex justify="center" my={6}>
                        <ButtonGroup isAttached size="md" variant="outline">
                            <Button
                                leftIcon={<Icon as={FaTh} />}
                                onClick={() => setViewMode('kanban')}
                                isActive={viewMode === 'kanban'}
                                colorScheme={viewMode === 'kanban' ? 'orange' : 'gray'}
                            >
                                Kanban
                            </Button>
                            <Button
                                leftIcon={<Icon as={FaList} />}
                                onClick={() => setViewMode('list')}
                                isActive={viewMode === 'list'}
                                colorScheme={viewMode === 'list' ? 'orange' : 'gray'}
                            >
                                Lista
                            </Button>
                            <Button
                                leftIcon={<Icon as={FaChartBar} />}
                                onClick={() => setViewMode('metricas')}
                                isActive={viewMode === 'metricas'}
                                colorScheme={viewMode === 'metricas' ? 'orange' : 'gray'}
                            >
                                Métricas
                            </Button>
                        </ButtonGroup>
                    </Flex>

                    {viewMode === 'metricas'
                        ? <DevolucoesMetricas />
                        : viewMode === 'list' ? renderListView() : renderKanbanView()}
                </Box>
            </Box>

            <Modal isOpen={isModalOpen} onClose={closeModal} size={{ base: "full", md: "5xl" }} isCentered scrollBehavior="inside">
                <ModalOverlay sx={{ '@media (max-width: 48em)': { alignItems: 'flex-start' } }} />
                <ModalContent sx={{ '@media (max-width: 48em)': { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, height: 'auto', maxHeight: '100%', m: 0 } }}>
                    <ModalHeader>
                        <Heading size={{ base: "sm", md: "md" }}>Detalhes da Devolução</Heading>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6} bg="gray.100" p={0} position="relative">

                        {isModalLoading && (
                            <Box
                                position="absolute" top="0" left="0" right="0" bottom="0"
                                bg="whiteAlpha.800" zIndex="overlay" display="flex"
                                alignItems="center" justifyContent="center" borderRadius="md"
                            >
                                <Spinner size="xl" color="orange.500" thickness="4px" />
                            </Box>
                        )}

                        {renderModalHeaderInfo()}

                        {selectedDevolucao && (
                            <Box p={{ base: 3, md: 6 }}>
                                <KanbanFullDetails
                                    devolucao={selectedDevolucao}
                                    onUpdate={handleUpdateInModal}
                                    onFileUpload={handleAnexarArquivo}
                                    onFileRemoved={handleArquivoRemovido}
                                    isLoading={isModalLoading}
                                    onIncluirEstoque={handleIncluirEstoque}
                                    onReverterEstoque={handleReverterEstoque}
                                    onFinalizarManual={handleFinalizarManual}
                                    onRevisaoOk={handleRevisaoOk}
                                    onRevisaoFalha={handleRevisaoFalha}
                                    onConsultarCustoEnvio={handleConsultarCustoEnvio}
                                    onSincronizar={handleSincronizarDevolucao}
                                    onEditarManual={() => setIsEditarManualOpen(true)}
                                    onExcluirManual={handleExcluirManual}
                                    onUpdateManualStatus={handleAtualizarStatusManual}
                                    onAfterDivergenciaChange={(id) => handlePesquisar(viewMode === 'list' ? pagina : 1, id)}
                                    onReembolsoConcluido={(id) => handlePesquisar(viewMode === 'list' ? pagina : 1, id)}
                                />
                            </Box>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            <ConfirmPortal />
        </>
    );
};

export default DevolucoesPage;
